import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { addSeconds } from 'date-fns';

import { call } from '@spacefill/shared/src/utils/Backend';
import { sendEvent } from '@spacefill/shared/src/utils/Tagging';
import { getWarehouseLabel } from '@spacefill/shared/src/utils/Warehouse';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { ButtonText } from '@spacefill/uikit/src/components/ButtonText/ButtonText';
import { Divider } from '@spacefill/uikit/src/components/Divider';
import { EmptyState } from '@spacefill/uikit/src/components/EmptyState';
import { InAppNotification } from '@spacefill/uikit/src/components/InAppNotification';
import { Loader } from '@spacefill/uikit/src/components/Loader';
import { SkeletonRow } from '@spacefill/uikit/src/components/Skeletons/SkeletonRow';
import { toast } from '@spacefill/uikit/src/components/Toast/createToast';

import emptyNotificationSVG from '../../assets/empty-states/empty-state-notification.svg';
import { templates } from './InAppNotificationsTemplates';

const PER_PAGE = 20;

function NotificationSkeleton() {
    return (
        <div className='py-3'>
            <SkeletonRow
                rows={[['w-1/3', 'w-1/3'], ['w-1/3', 'w-2/5', 'w-20'], 'w-full', 'w-1/2']}
                height='h-4'
            />
        </div>
    );
}

export function InAppNotificationsList({ unreadCount, filter = 'all' }) {
    const { user } = useUserContext();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [expandedGroupId, setExpandedGroupId] = useState(null);
    const mainListRef = useRef(null);
    const groupListRef = useRef(null);
    const hasAutoScrolledRef = useRef(false);
    const hasAutoExpandedRef = useRef(false);

    const plannedDate = searchParams.get('planned_date');
    const selectedNotificationId = searchParams.get('id');

    const queryParams = { limit: PER_PAGE };
    if (filter === 'unread') queryParams.readStatus = 'unread';
    if (filter === 'read') queryParams.readStatus = 'read';

    const { data, isFetchingNextPage, hasNextPage, fetchNextPage, isFetching, isLoading } = useInfiniteQuery({
        queryKey: ['/in-app-notifications/list', queryParams, plannedDate, selectedNotificationId],
        queryFn: async ({ pageParam = 0 }) => {
            const response = await call('/in-app-notifications/list', {
                ...queryParams,
                offset: pageParam,
                plannedDateBefore: new Date().toISOString(),
            });
            if (
                selectedNotificationId &&
                pageParam === 0 &&
                !response.data.some((n) => n.id === selectedNotificationId)
            ) {
                // If the selected notification is not in the first page, we need to fetch from the planneDate of the selected notification to get it and its surrounding notifications
                const responsePlanned = await call('/in-app-notifications/list', {
                    plannedDateBefore: plannedDate,
                });
                return { ...responsePlanned, oldMode: true };
            }
            return { ...response, oldMode: false };
        },
        getNextPageParam: (lastPage) => {
            return lastPage.nextPage ?? undefined; // undefined is important to have `hasNextPage` false
        },
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        hasAutoScrolledRef.current = false;
        hasAutoExpandedRef.current = false;
    }, [filter]);

    useEffect(() => {
        hasAutoScrolledRef.current = false;
        hasAutoExpandedRef.current = false;
    }, [selectedNotificationId]);

    useEffect(() => {
        if (isLoading || !selectedNotificationId) return;

        if (!hasAutoExpandedRef.current) {
            const allNotifications = data?.pages?.flatMap((p) => p.data || []) || [];
            const selected = allNotifications.find((n) => n.id === selectedNotificationId);
            if (selected?.type === 'grouped') {
                hasAutoExpandedRef.current = true;
                setExpandedGroupId(selected.id);
                return;
            }
        }

        if (!hasAutoScrolledRef.current && mainListRef.current) {
            const el = mainListRef.current.querySelector(`[data-notification-id="${selectedNotificationId}"]`);
            if (el) {
                hasAutoScrolledRef.current = true;
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [isLoading, selectedNotificationId, data]);

    const { mutate: readMutate } = useMutation({
        mutationFn: ({ notificationId }) => call(`/in-app-notifications/${notificationId}/read`, {}, 'POST'),
        async onSuccess() {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ['/in-app-notifications/list'],
                }),
                queryClient.invalidateQueries({
                    queryKey: ['/in-app-notifications/unread'],
                }),
            ]);
        },
        onError(error) {
            console.error('error during read mutation: ', error);
            toast.error(t('Internal error!'));
        },
    });

    const { mutate: unreadMutate } = useMutation({
        mutationFn: ({ notificationId }) => call(`/in-app-notifications/${notificationId}/unread`, {}, 'POST'),
        async onSuccess() {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ['/in-app-notifications/list'],
                }),
                queryClient.invalidateQueries({
                    queryKey: ['/in-app-notifications/unread'],
                }),
            ]);
        },
        onError(error) {
            console.error('error during unread mutation: ', error);
            toast.error(t('Internal error!'));
        },
    });

    const observer = useRef();
    const lastNotificationElementRef = useCallback(
        (node) => {
            if (isFetchingNextPage) return;
            if (plannedDate) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new window.IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasNextPage) {
                    fetchNextPage();
                }
            });
            if (node) observer.current.observe(node);
        },
        [isFetchingNextPage, fetchNextPage, hasNextPage, plannedDate]
    );

    const totalCount = data?.pages?.[0]?.count;
    const loadedCount = data?.pages?.reduce((acc, page) => acc + (page.data?.length || 0), 0) || 0;
    const remainingCount = totalCount - loadedCount;
    const skeletonCount = isLoading ? Math.min(unreadCount || 4, 4) : isFetching ? Math.min(remainingCount || 4, 4) : 0;

    const handleShowRecentNotifications = () => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('planned_date');
            next.delete('id');
            return next;
        });
        setExpandedGroupId(null);
        sendEvent('show-recent-notification');
    };

    const handleMarkAsRead = (notification) => {
        if (!notification.readAt) {
            sendEvent('mark-as-read');
            readMutate({ notificationId: notification.id });
        }
    };

    const handleMarkAsUnread = (notification) => {
        if (!notification.readAt) {
            sendEvent('mark-as-unread');
            unreadMutate({ notificationId: notification.id });
        }
    };

    let emptyMessage = '';
    if (filter === 'all' && data?.pages?.[0]?.data?.length === 0) {
        emptyMessage = t('No notifications yet.');
    }
    if (filter === 'unread' && data?.pages?.[0]?.data?.length === 0) {
        emptyMessage = t('No unread notifications.');
    }
    if (filter === 'read' && data?.pages?.[0]?.data?.length === 0) {
        emptyMessage = t('No read notifications.');
    }
    if (emptyMessage) {
        return (
            <EmptyState
                picture={{
                    src: emptyNotificationSVG,
                    width: 80,
                    height: 121,
                }}
                paragraph={emptyMessage}
            />
        );
    }

    return (
        <div className='flex flex-col h-full -mx-6 pl-6 pr-8 -mb-6'>
            {plannedDate && data?.pages?.[0]?.oldMode ? (
                <ButtonText
                    className='self-start m-1 -ml-5'
                    type='button'
                    variant='secondary'
                    onClick={() => handleShowRecentNotifications()}
                >
                    <FontAwesomeIcon
                        icon={faChevronRight}
                        className='rotate-180'
                    />
                    <span className='font-medium'>{t('Show recent notifications')}</span>
                </ButtonText>
            ) : expandedGroupId ? (
                <ButtonText
                    type='button'
                    className='self-start m-1 -ml-5'
                    variant='secondary'
                    onClick={() => handleShowRecentNotifications()}
                >
                    <FontAwesomeIcon
                        icon={faChevronRight}
                        className='rotate-180'
                    />
                    <span className='text-sm font-medium'>{t('Back')}</span>
                </ButtonText>
            ) : null}

            {/* Main notifications list */}
            <div
                ref={mainListRef}
                className={`flex-1 overflow-auto -mx-6 pl-6 pr-8 -mb-6 ${expandedGroupId ? 'hidden' : ''}`}
            >
                {!isLoading && totalCount === 0 && (
                    <EmptyState
                        picture={{
                            src: emptyNotificationSVG,
                            width: 80,
                            height: 121,
                        }}
                        paragraph={t('No notification yet. New comments and alerts will be displayed here.')}
                    />
                )}
                {(data?.pages || []).map((notifications, pageIndex) => {
                    return (
                        <Fragment key={pageIndex}>
                            {(notifications.data || []).map((notification) => {
                                if (!(notification.templateId in templates)) {
                                    return null;
                                }
                                const notificationTemplate = templates[notification.templateId];
                                const dataAttributes = {
                                    'data-notification-id': notification.id,
                                    'data-permalink': `/notifications/?id=${notification.id}&planned_date=${addSeconds(new Date(notification.plannedDate), 5).toISOString()}`,
                                };
                                if (notification.type === 'grouped') {
                                    const handleGroupClick = () => {
                                        sendEvent('show-group-notification');
                                        setExpandedGroupId(notification.id);
                                        readMutate({ notificationId: notification.id });
                                    };

                                    const firstItem = notification.items?.[0];
                                    const hasUnread = notification.items?.some((item) => !item.readAt);

                                    return (
                                        <div
                                            key={`InAppNotificationGroup-${notification.id}`}
                                            onClick={handleGroupClick}
                                            className='cursor-pointer -mx-6 relative'
                                        >
                                            <InAppNotification
                                                highlighted={
                                                    notification.id === selectedNotificationId &&
                                                    !!selectedNotificationId
                                                }
                                                titleIcon={notificationTemplate.icon(notification)}
                                                user={notificationTemplate.user({
                                                    templateParams: firstItem?.templateParams,
                                                })}
                                                createdFromRuleId={
                                                    firstItem?.templateParams?.incident?.createdFromRuleId
                                                }
                                                receivedAt={new Date(notification.plannedDate)}
                                                unread={hasUnread}
                                                onMarkAsRead={() => {
                                                    handleMarkAsRead(notification);
                                                }}
                                                onMarkAsUnread={() => handleMarkAsUnread(notification)}
                                                dataTestid={`in-app-notification-group-${notification.templateId}`}
                                                htmlAttributes={dataAttributes}
                                            >
                                                {notificationTemplate.groupContent
                                                    ? notificationTemplate.groupContent(notification.count)
                                                    : notificationTemplate.content(
                                                          {
                                                              ...notification,
                                                              templateParams: firstItem?.templateParams,
                                                          },
                                                          user
                                                      )}{' '}
                                                <FontAwesomeIcon
                                                    icon={faChevronRight}
                                                    className='text-gray-400 mr-2'
                                                />
                                            </InAppNotification>
                                        </div>
                                    );
                                }

                                const handleClick = () => {
                                    readMutate({ notificationId: notification.id });
                                    sendEvent('click-notification');
                                    navigate(notificationTemplate.url(notification));
                                };

                                return (
                                    <div
                                        key={`InAppNotification-${notification.id}`}
                                        onClick={handleClick}
                                        role='button'
                                        className='cursor-pointer -mx-6'
                                        data-url={notificationTemplate.url(notification)}
                                    >
                                        <InAppNotification
                                            highlighted={
                                                notification.id === selectedNotificationId && !!selectedNotificationId
                                            }
                                            titleIcon={notificationTemplate.icon(notification)}
                                            user={notificationTemplate.user(notification)}
                                            receivedAt={new Date(notification.createdAt)}
                                            unread={!notification.readAt}
                                            onMarkAsRead={() => {
                                                handleMarkAsRead(notification);
                                            }}
                                            onMarkAsUnread={() => unreadMutate({ notificationId: notification.id })}
                                            createdFromRuleId={
                                                notification?.templateParams?.incident?.createdFromRuleId
                                            }
                                            dataTestid={`in-app-notification-${notification.templateId}`}
                                            htmlAttributes={dataAttributes}
                                        >
                                            {notificationTemplate.content(notification, user)}
                                        </InAppNotification>
                                    </div>
                                );
                            })}
                        </Fragment>
                    );
                })}
                {(isLoading || isFetching) && skeletonCount > 0 && (
                    <>
                        {Array.from({ length: skeletonCount }).map((_, index) => (
                            <Fragment key={index}>
                                <NotificationSkeleton />
                                {index < skeletonCount - 1 && <Divider />}
                            </Fragment>
                        ))}
                    </>
                )}
                {!plannedDate && hasNextPage && (
                    <div ref={lastNotificationElementRef}>
                        <Loader />
                    </div>
                )}
            </div>

            {/* Group children list */}
            <div
                ref={groupListRef}
                className={`flex-1 overflow-auto -mx-6 pl-6 pr-8 -mb-6 ${!expandedGroupId ? 'hidden' : ''}`}
            >
                {(() => {
                    const groupNotification = data?.pages
                        ?.flatMap((p) => p.data || [])
                        ?.find((n) => n.type === 'grouped' && n.id === expandedGroupId);

                    if (!groupNotification) return null;

                    return (groupNotification.items || []).map((item) => {
                        const notification = {
                            ...item,
                            templateId: groupNotification.templateId,
                        };
                        if (!(notification.templateId in templates)) {
                            return null;
                        }
                        const notificationTemplate = templates[notification.templateId];

                        const handleClick = () => {
                            readMutate({ notificationId: notification.id });
                            sendEvent('click-notification-in-group');
                            navigate(notificationTemplate.url(notification));
                        };
                        const dataAttributes = {
                            'data-notification-id': notification.id,
                        };

                        return (
                            <div
                                key={`InAppNotification-item-${notification.id}`}
                                onClick={handleClick}
                                className='cursor-pointer -mx-6'
                                role='button'
                                data-url={notificationTemplate.url(notification)}
                            >
                                <InAppNotification
                                    key={item.id}
                                    isNotificationChild
                                    title={notificationTemplate.title(notification)}
                                    titleIcon={notificationTemplate.icon(notification)}
                                    user={notificationTemplate.user(notification)}
                                    createdFromRuleId={notification?.templateParams?.incident?.createdFromRuleId}
                                    receivedAt={new Date(item.createdAt)}
                                    warehouseName={getWarehouseLabel(user, notification.templateParams.warehouse)}
                                    unread={!item.readAt}
                                    onMarkAsRead={() => {
                                        handleMarkAsRead(notification);
                                    }}
                                    dataTestid={`in-app-notification-${notification.templateId}`}
                                    htmlAttributes={dataAttributes}
                                >
                                    {notificationTemplate.content(notification, user)}
                                </InAppNotification>
                            </div>
                        );
                    });
                })()}
            </div>
        </div>
    );
}
